import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, money } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Zap, LogOut, Users, ReceiptText, Banknote, TrendingUp, Ban, CheckCircle2, Trash2, Check, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import Logo from "../components/Logo";

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-4">{label}</p>
      <p className="font-display text-2xl font-extrabold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

const badge = (status) => `text-xs font-bold px-2 py-1 rounded-full ${
  status === "approved" || status === "rewarded" || status === "paid" || status === "completed" || status === "active" ? "bg-emerald-50 text-emerald-700" :
  status === "pending" || status === "unpaid" || status === "contacted" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`;

const TH = ({ children, right }) => <th className={`px-5 py-3 font-bold ${right ? "text-right" : ""}`}>{children}</th>;
const Table = ({ head, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500"><tr>{head}</tr></thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState({ stats: null, users: [], bills: [], payouts: [], referrals: [], categories: [], providers: [], offers: [], switches: [], payments: [], rules: null });
  const [offerDialog, setOfferDialog] = useState(null);
  const [provDialog, setProvDialog] = useState(null);
  const sym = d.stats?.currency_symbol || "$";

  const loadAll = useCallback(async () => {
    const keys = ["stats", "users", "bills", "payouts", "referrals", "categories", "providers", "offers", "switch-requests", "payments", "rules"];
    const res = await Promise.all(keys.map((k) => api.get(`/admin/${k}`)));
    setD({ stats: res[0].data, users: res[1].data, bills: res[2].data, payouts: res[3].data, referrals: res[4].data,
      categories: res[5].data, providers: res[6].data, offers: res[7].data, switches: res[8].data, payments: res[9].data, rules: res[10].data });
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo textClassName="text-white" />
            <span className="text-emerald-400 font-display font-extrabold text-sm bg-white/10 px-2 py-0.5 rounded-md">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-300 hidden sm:block">{user?.email}</span>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="text-slate-300 hover:text-white hover:bg-white/10"><LogOut className="w-5 h-5" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {d.stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KPI icon={Users} label="Total users" value={d.stats.total_users} color="bg-slate-100 text-slate-700" />
            <KPI icon={ReceiptText} label="Bills / Paid" value={`${d.stats.total_bills} / ${d.stats.paid_bills}`} color="bg-emerald-50 text-emerald-600" />
            <KPI icon={TrendingUp} label="Savings generated" value={money(d.stats.total_saved, sym)} color="bg-amber-50 text-amber-600" />
            <KPI icon={Banknote} label="Collected" value={money(d.stats.total_collected, sym)} color="bg-slate-100 text-slate-700" />
          </div>
        )}

        <Tabs defaultValue="overview">
          <TabsList className="bg-white border border-slate-200 rounded-2xl p-1 mb-6 flex-wrap h-auto gap-1">
            {["overview", "users", "categories", "providers", "offers", "bills", "payments", "switches", "payouts", "rules"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="rounded-xl capitalize data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                {t}{t === "payouts" && d.stats?.pending_payouts > 0 && <span className="ml-1 bg-amber-500 text-white rounded-full px-1.5 text-xs">{d.stats.pending_payouts}</span>}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-display font-bold text-slate-900 mb-4">Signups — last 7 days</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={d.stats?.signups_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="signups" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Table head={<><TH>Name</TH><TH>Email</TH><TH>Balance</TH><TH>Points</TH><TH>Status</TH><TH right>Actions</TH></>}>
              {d.users.filter((u) => u.role !== "admin").map((u) => (
                <tr key={u.id} data-testid={`admin-user-${u.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-900">{u.name}</td>
                  <td className="px-5 py-3 text-slate-600">{u.email}</td>
                  <td className="px-5 py-3 font-semibold text-emerald-600">{money(u.wallet_balance, sym)}</td>
                  <td className="px-5 py-3 text-slate-600">{u.reward_points || 0}</td>
                  <td className="px-5 py-3"><span className={badge(u.suspended ? "rejected" : "active")}>{u.suspended ? "suspended" : "active"}</span></td>
                  <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                    {u.suspended
                      ? <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/users/${u.id}`, { action: "activate" }), "Activated")} className="text-emerald-600"><CheckCircle2 className="w-4 h-4" /></Button>
                      : <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/users/${u.id}`, { action: "suspend" }), "Suspended")} className="text-amber-600"><Ban className="w-4 h-4" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/users/${u.id}`, { action: "delete" }), "Deleted")} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="categories">
            <Table head={<><TH>Name</TH><TH>Slug</TH><TH>Subcategories</TH><TH>Coming soon</TH><TH right>Active</TH></>}>
              {d.categories.map((c) => (
                <tr key={c.id} data-testid={`admin-cat-${c.slug}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-900">{c.name}</td>
                  <td className="px-5 py-3 text-slate-500">{c.slug}</td>
                  <td className="px-5 py-3 text-slate-500 max-w-xs truncate">{(c.subcategories || []).join(", ") || "—"}</td>
                  <td className="px-5 py-3"><Switch checked={c.coming_soon} onCheckedChange={(v) => act(() => api.put(`/admin/categories/${c.id}`, { coming_soon: v }), "Updated")} data-testid={`cat-soon-${c.slug}`} /></td>
                  <td className="px-5 py-3 text-right"><Switch checked={c.active} onCheckedChange={(v) => act(() => api.put(`/admin/categories/${c.id}`, { active: v }), "Updated")} data-testid={`cat-active-${c.slug}`} /></td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="providers">
            <div className="flex justify-end mb-3">
              <Button onClick={() => setProvDialog({ category_slug: d.categories[0]?.slug || "internet", name: "", discount_percent: 5, cashback_percent: 2, reward_points: 500, supports_switch: false, active: true })} data-testid="add-provider-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 font-semibold"><Plus className="w-4 h-4 mr-1" /> Add provider</Button>
            </div>
            <Table head={<><TH>Provider</TH><TH>Category</TH><TH>Discount %</TH><TH>Cashback %</TH><TH>Points</TH><TH right>Actions</TH></>}>
              {d.providers.map((p) => (
                <tr key={p.id} data-testid={`admin-prov-${p.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-900">{p.name}</td>
                  <td className="px-5 py-3 text-slate-500">{p.category_slug}</td>
                  <td className="px-5 py-3">{p.discount_percent}%</td>
                  <td className="px-5 py-3">{p.cashback_percent}%</td>
                  <td className="px-5 py-3">{p.reward_points}</td>
                  <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setProvDialog(p)} className="text-slate-600">Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/admin/providers/${p.id}`), "Deleted")} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="offers">
            <div className="flex justify-end mb-3">
              <Button onClick={() => setOfferDialog({ category_slug: d.categories[0]?.slug || "internet", title: "", description: "", current_price: 0, new_price: 0, reward_points: 0, cashback: 0, period: "month", active: true })} data-testid="add-offer-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 font-semibold"><Plus className="w-4 h-4 mr-1" /> Add offer</Button>
            </div>
            <Table head={<><TH>Title</TH><TH>Category</TH><TH>Current</TH><TH>New</TH><TH>Saving</TH><TH>Points</TH><TH right>Actions</TH></>}>
              {d.offers.map((o) => (
                <tr key={o.id} data-testid={`admin-offer-${o.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-900">{o.title}</td>
                  <td className="px-5 py-3 text-slate-500">{o.category_slug}</td>
                  <td className="px-5 py-3">{money(o.current_price, sym)}</td>
                  <td className="px-5 py-3 text-emerald-600 font-semibold">{money(o.new_price, sym)}</td>
                  <td className="px-5 py-3 text-amber-600">{money(o.saving, sym)}</td>
                  <td className="px-5 py-3">{o.reward_points}</td>
                  <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setOfferDialog(o)} className="text-slate-600">Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/admin/offers/${o.id}`), "Deleted")} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="bills">
            <Table head={<><TH>User</TH><TH>Category</TH><TH>Provider</TH><TH>Amount</TH><TH>Status</TH></>}>
              {d.bills.map((b) => (
                <tr key={b.id} data-testid={`admin-bill-${b.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3"><p className="font-semibold text-slate-900">{b.user_name}</p><p className="text-xs text-slate-400">{b.user_email}</p></td>
                  <td className="px-5 py-3 text-slate-600">{b.category_name}</td>
                  <td className="px-5 py-3 text-slate-600">{b.provider_name}</td>
                  <td className="px-5 py-3 text-slate-900">{money(b.amount, sym)}</td>
                  <td className="px-5 py-3"><span className={badge(b.status)}>{b.status}</span></td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="payments">
            <Table head={<><TH>User</TH><TH>Provider</TH><TH>Original</TH><TH>Discount</TH><TH>Paid</TH><TH>Points</TH></>}>
              {d.payments.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No payments yet</td></tr>}
              {d.payments.map((p) => (
                <tr key={p.id} data-testid={`admin-payment-${p.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-900">{p.user_name}</td>
                  <td className="px-5 py-3 text-slate-600">{p.provider_name}</td>
                  <td className="px-5 py-3 text-slate-600">{money(p.original_amount, sym)}</td>
                  <td className="px-5 py-3 text-emerald-600">{money(p.discount, sym)}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{money(p.amount_paid, sym)}</td>
                  <td className="px-5 py-3">{p.reward_points}</td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="switches">
            <Table head={<><TH>User</TH><TH>Category</TH><TH>To</TH><TH>Current → New</TH><TH>Status</TH><TH right>Actions</TH></>}>
              {d.switches.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No switch requests yet</td></tr>}
              {d.switches.map((s) => (
                <tr key={s.id} data-testid={`admin-switch-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3"><p className="font-semibold text-slate-900">{s.user_name}</p><p className="text-xs text-slate-400">{s.user_email}</p></td>
                  <td className="px-5 py-3 text-slate-600">{s.category_slug}</td>
                  <td className="px-5 py-3 text-slate-600">{s.to_provider_name}</td>
                  <td className="px-5 py-3 text-slate-600">{money(s.current_price, sym)} → {money(s.recommended_price, sym)}</td>
                  <td className="px-5 py-3"><span className={badge(s.status)}>{s.status}</span></td>
                  <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                    {s.status === "pending" && <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/switch-requests/${s.id}`, { status: "contacted" }), "Marked contacted")} className="text-amber-600">Contacted</Button>}
                    {s.status !== "completed" && <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/switch-requests/${s.id}`, { status: "completed" }), "Completed")} className="text-emerald-600"><Check className="w-4 h-4" /></Button>}
                    {s.status !== "rejected" && <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/switch-requests/${s.id}`, { status: "rejected" }), "Rejected")} className="text-red-600"><X className="w-4 h-4" /></Button>}
                  </td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="payouts">
            <Table head={<><TH>User</TH><TH>Amount</TH><TH>Method</TH><TH>Details</TH><TH>Status</TH><TH right>Actions</TH></>}>
              {d.payouts.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No payout requests</td></tr>}
              {d.payouts.map((p) => (
                <tr key={p.id} data-testid={`admin-payout-${p.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3"><p className="font-semibold text-slate-900">{p.user_name}</p><p className="text-xs text-slate-400">{p.user_email}</p></td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{money(p.amount, sym)}</td>
                  <td className="px-5 py-3 text-slate-600">{p.method}</td>
                  <td className="px-5 py-3 text-slate-600">{p.account_details}</td>
                  <td className="px-5 py-3"><span className={badge(p.status)}>{p.status}</span></td>
                  <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                    {p.status === "pending" && <>
                      <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/payouts/${p.id}`, { action: "approve" }), "Approved")} className="text-emerald-600"><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/payouts/${p.id}`, { action: "reject" }), "Rejected")} className="text-red-600"><X className="w-4 h-4" /></Button>
                    </>}
                  </td>
                </tr>
              ))}
            </Table>
          </TabsContent>

          <TabsContent value="rules">
            {d.rules && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-xl">
                <h3 className="font-display font-bold text-slate-900 mb-4">Referral & payout rules ({sym})</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[["referrer_reward", "Referrer bonus"], ["referee_reward", "Referee bonus"], ["payout_threshold", "Payout threshold"]].map(([k, label]) => (
                    <div key={k}>
                      <Label className="font-semibold text-sm">{label}</Label>
                      <Input type="number" value={d.rules[k]} onChange={(e) => setD({ ...d, rules: { ...d.rules, [k]: parseFloat(e.target.value) || 0 } })} data-testid={`rule-${k}`} className="mt-1.5 bg-slate-50" />
                    </div>
                  ))}
                </div>
                <Button onClick={() => act(() => api.put("/admin/rules", d.rules), "Rules saved")} data-testid="save-rules-btn" className="mt-5 rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold">Save rules</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Provider dialog */}
      <Dialog open={!!provDialog} onOpenChange={(o) => !o && setProvDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{provDialog?.id ? "Edit" : "Add"} provider</DialogTitle></DialogHeader>
          {provDialog && (
            <div className="space-y-3">
              <div><Label className="text-sm font-semibold">Category</Label>
                <select value={provDialog.category_slug} onChange={(e) => setProvDialog({ ...provDialog, category_slug: e.target.value })} className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-md h-10 px-3 text-sm" data-testid="prov-category">
                  {d.categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div><Label className="text-sm font-semibold">Name</Label><Input value={provDialog.name} onChange={(e) => setProvDialog({ ...provDialog, name: e.target.value })} data-testid="prov-name" className="mt-1 bg-slate-50" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-sm font-semibold">Disc %</Label><Input type="number" value={provDialog.discount_percent} onChange={(e) => setProvDialog({ ...provDialog, discount_percent: parseFloat(e.target.value) || 0 })} className="mt-1 bg-slate-50" /></div>
                <div><Label className="text-sm font-semibold">Cash %</Label><Input type="number" value={provDialog.cashback_percent} onChange={(e) => setProvDialog({ ...provDialog, cashback_percent: parseFloat(e.target.value) || 0 })} className="mt-1 bg-slate-50" /></div>
                <div><Label className="text-sm font-semibold">Points</Label><Input type="number" value={provDialog.reward_points} onChange={(e) => setProvDialog({ ...provDialog, reward_points: parseInt(e.target.value) || 0 })} className="mt-1 bg-slate-50" /></div>
              </div>
              <Button onClick={() => act(async () => { const p = provDialog; setProvDialog(null); if (p.id) await api.put(`/admin/providers/${p.id}`, p); else await api.post("/admin/providers", p); }, "Saved")} data-testid="prov-save" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Offer dialog */}
      <Dialog open={!!offerDialog} onOpenChange={(o) => !o && setOfferDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{offerDialog?.id ? "Edit" : "Add"} offer</DialogTitle></DialogHeader>
          {offerDialog && (
            <div className="space-y-3">
              <div><Label className="text-sm font-semibold">Category</Label>
                <select value={offerDialog.category_slug} onChange={(e) => setOfferDialog({ ...offerDialog, category_slug: e.target.value })} className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-md h-10 px-3 text-sm" data-testid="offer-category">
                  {d.categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div><Label className="text-sm font-semibold">Title</Label><Input value={offerDialog.title} onChange={(e) => setOfferDialog({ ...offerDialog, title: e.target.value })} data-testid="offer-title" className="mt-1 bg-slate-50" /></div>
              <div><Label className="text-sm font-semibold">Description</Label><Input value={offerDialog.description} onChange={(e) => setOfferDialog({ ...offerDialog, description: e.target.value })} className="mt-1 bg-slate-50" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-sm font-semibold">Current price</Label><Input type="number" value={offerDialog.current_price} onChange={(e) => setOfferDialog({ ...offerDialog, current_price: parseFloat(e.target.value) || 0 })} className="mt-1 bg-slate-50" /></div>
                <div><Label className="text-sm font-semibold">New price</Label><Input type="number" value={offerDialog.new_price} onChange={(e) => setOfferDialog({ ...offerDialog, new_price: parseFloat(e.target.value) || 0 })} className="mt-1 bg-slate-50" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-sm font-semibold">Reward points</Label><Input type="number" value={offerDialog.reward_points} onChange={(e) => setOfferDialog({ ...offerDialog, reward_points: parseInt(e.target.value) || 0 })} className="mt-1 bg-slate-50" /></div>
                <div><Label className="text-sm font-semibold">Period</Label><Input value={offerDialog.period} onChange={(e) => setOfferDialog({ ...offerDialog, period: e.target.value })} className="mt-1 bg-slate-50" /></div>
              </div>
              <Button onClick={() => act(async () => { const o = offerDialog; setOfferDialog(null); if (o.id) await api.put(`/admin/offers/${o.id}`, o); else await api.post("/admin/offers", o); }, "Saved")} data-testid="offer-save" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
